import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Box, styled, useTheme } from '@mui/material';
import { Controller } from 'react-hook-form';
import { Trans, useTranslation } from 'react-i18next';
import dayjs from 'dayjs';
import { v4 as uuidv4 } from 'uuid';
import { useAppSelector, useAppDispatch, useStalePlanRefresh } from '../../common/hooks';
import { TextboxInput, Typography } from '../../components/atoms';
import { EditableTable } from '../../components/molecules/EditableTable';
import type { EditableColumnDef, RowEditContext } from '../../components/molecules/EditableTable/types';
import { DatePickerFTDS } from '../../components/molecules/DatePickerFTDS';
import { Banner } from '../../components/molecules/Banner/Banner';
import { PdfDownloadErrorBanner } from '../../components/molecules/PdfDownloadErrorBanner/PdfDownloadErrorBanner';
import { StalePlanBanner } from '../../components/molecules/StalePlanBanner/StalePlanBanner';
import { ConfirmationCentered } from '../../components/molecules/ConfirmationDialog/ConfirmationDialog';
import {
    CLIENT_PROFILE_STAGE_KEYS,
    DEFAULT_RETIREMENT_AGE,
    EWS_DURATION_KEYS,
    MAX_ALLOWED_AGE_FF,
    MAX_ALLOWED_RETIREMENT_AGE,
    MAX_RETIREMENT_AGE_WARNING,
    RETIREMENT_GOAL,
    PARTICIPANT_ID_AVAILABLE_TENANTS,
    STATUSVARIANTS,
    FEATURE_NAMES
} from '../../common/constants';
import { createClient, saveClient } from '../../features/client';
import { saveGoal, syncAdviceGoals } from '../../features/client-goals';
import { setAffectedEwsTerms } from '../../features/ews';
import type { AdviceGoalPayloadType } from '../../features/client-goals';
import { setHasUnsavedProfileChanges } from '../../features/global/profileEditSlice';
import { checkFFEnabled, getClientJourneyFlowType } from '../../utils';
import { normalizeAge } from '../../features/advisorConsole/utils';
import { Account, AdviceClient, GoalTypes, TargetDateKeys } from '../../common/types/clients-entity';
import { addAgeToDate } from '../../common/helper';
import { startLoading, stopLoading } from '../../features/global/appLoader';

// Profile DOB is validated to 18-120 years; scoped here so the shared MIN_ALLOWED_AGE_FF (legacy drawer) is untouched.
const MIN_ALLOWED_PROFILE_AGE = 18;

interface PersonalInfoCardInlineProps {
    editingRowKey: string | number | null;
    // eslint-disable-next-line no-unused-vars
    onEditingRowKeyChange: (key: string | number | null) => void;
    isAddingNew: boolean;
    onAddCoClient: () => void;
    onCancelAdd: () => void;
}

interface ProfileRow {
    id: 'client' | 'coClient';
    type: 'Client' | 'Co-Client';
    firstName: string;
    lastName: string;
    dob: string; // 'DD-MM-YYYY' or ''
    retirementAge: string;
    planningAge: string;
    [key: string]: unknown;
}

const SubHeader = styled(Typography)(({ theme }) => ({
    display: 'block',
    fontWeight: 700,
    color: theme.palette.semantic.text.default,
    padding: theme.spacing(1, 0, 4)
}));

const BannerWrapper = styled('div')(({ theme }) => ({
    paddingBlock: theme.spacing(1)
}));

const emptyClientRow: ProfileRow = {
    id: 'client',
    type: 'Client',
    customerId: '',
    firstName: '',
    lastName: '',
    dob: '',
    retirementAge: '',
    planningAge: ''
};

const emptyCoClientRow: ProfileRow = {
    id: 'coClient',
    type: 'Co-Client',
    customerId: '',
    firstName: '',
    lastName: '',
    dob: '',
    retirementAge: '',
    planningAge: ''
};

const NAME_PATTERN = /^(?!\s)(?!.*\s$)([A-Za-z0-9\s’'.\-&/+()áéíóúüñÁÉÍÓÚÜÑàâäèêëîïôöùûçÀÂÄÈÊËÎÏÔÖÙÛÇ])*$/i;

// Alphanumeric characters and hyphens (-) only are the only allowed special character - for Customer ID validation.
const CUSTOMER_ID_PATTERN = /^[A-Za-z0-9]+(?:-[A-Za-z0-9]+)*-?$/;

const runAfterPointerGesture = (run: () => void): void => {
    let done = false;
    let timer: ReturnType<typeof setTimeout>;
    const finish = () => {
        if (done) return;
        done = true;
        clearTimeout(timer);
        document.removeEventListener('click', finish);
        run();
    };
    document.addEventListener('click', finish);
    timer = setTimeout(finish, 100);
};

// Derives the calendar month/year a person reaches the given age, based on their DOB
// (stored as 'DD-MM-YYYY'). Shown below the age inputs, e.g. DOB 02/1965 + age 65 -> "Feb 2030".
const calculateAgeYearLabel = (dob: string, age: string): string => {
    if (!dob || !age) return '';
    const birthDate = dayjs(dob, 'DD-MM-YYYY');
    if (!birthDate.isValid()) return '';
    const ageNum = parseInt(age, 10);
    if (Number.isNaN(ageNum)) return '';
    return birthDate.add(ageNum, 'year').format('MMM YYYY');
};

// DOB month/year picker bound to react-hook-form. A typed, invalid entry (e.g. a 0000 year) never
// reaches onChange, so the validate rule never runs; the picker's onError is registered directly to
// gate Save. Errors are only enforced after the user interacts (focus), so prefilled data is ignored.
const DobDatePickerField: React.FC<{
    ctx: RowEditContext<ProfileRow>;
    // eslint-disable-next-line no-unused-vars
    onRequestConfirm: (resume: () => void) => void;
    canOpen: () => boolean;
}> = ({ ctx, onRequestConfirm, canOpen }) => {
    const { t } = useTranslation();
    const maxDobDate = useMemo(() => dayjs().subtract(1, 'month').endOf('month'), []);
    const currentMonth = useMemo(() => dayjs().startOf('month'), []);
    const pickerErrorRef = React.useRef(false);
    const hasInteractedRef = React.useRef(false);
    const wrapperRef = React.useRef<HTMLDivElement>(null);
    const handleCanOpen = () => {
        if (canOpen()) return true;
        onRequestConfirm(() => wrapperRef.current?.querySelector('button')?.click());
        return false;
    };
    return (
        <Controller
            name="dob"
            control={ctx.control}
            rules={{
                required: { value: true, message: t('BIRTHDATE_REQUIRED') },
                validate: (value) => {
                    const parsed = value ? dayjs(value as string, 'DD-MM-YYYY') : null;
                    if (!parsed || !parsed.isValid()) {
                        return t('TEXT_THIS_IS_REQUIRED_FIELD');
                    }
                    const minDate = dayjs().subtract(MAX_ALLOWED_AGE_FF, 'year');
                    const maxDate = dayjs().subtract(MIN_ALLOWED_PROFILE_AGE, 'year');
                    if (parsed.isBefore(minDate) || parsed.isAfter(maxDate)) {
                        return t('AGE_RANGE_VALIDATION_ERROR', {
                            minAge: MIN_ALLOWED_PROFILE_AGE,
                            maxAge: MAX_ALLOWED_AGE_FF
                        });
                    }
                    return true;
                }
            }}
            render={({ field: f, fieldState }) => {
                const fieldErrorMsg = fieldState.error?.message as string | undefined;
                return (
                    <Box
                        ref={wrapperRef}
                        sx={{ display: 'contents' }}
                        onFocus={(e: React.FocusEvent<HTMLDivElement>) => {
                            hasInteractedRef.current = true;
                            const target = e.target;
                            if (target instanceof HTMLInputElement) onRequestConfirm(() => target.focus());
                        }}
                    >
                        <DatePickerFTDS
                            selectionType="month"
                            canOpen={handleCanOpen}
                            value={f.value ? dayjs(f.value as string, 'DD-MM-YYYY') : null}
                            endDate={maxDobDate}
                            referenceDate={currentMonth}
                            error={!!fieldErrorMsg || !!fieldState.error}
                            minDateErrorMsg={fieldErrorMsg}
                            onChange={(d) => {
                                f.onChange(d ? d.startOf('month').format('DD-MM-YYYY') : '');
                                const dependents = (['retirementAge', 'planningAge'] as const).filter((name) =>
                                    String(ctx.getValues(name) ?? '').trim()
                                );
                                if (dependents.length) void ctx.trigger(dependents);
                            }}
                            onValidationError={(pickerError?: string | null) => {
                                // Skip prefilled/initial picker errors; only enforce after the user
                                // has interacted with the field (typing an invalid 0000 included).
                                if (!hasInteractedRef.current) return;
                                if (pickerError) {
                                    pickerErrorRef.current = true;
                                    ctx.setError('dob', {
                                        type: 'manual',
                                        message: t('TEXT_THIS_IS_REQUIRED_FIELD')
                                    });
                                } else if (pickerErrorRef.current) {
                                    pickerErrorRef.current = false;
                                    void ctx.trigger('dob');
                                }
                            }}
                        />
                    </Box>
                );
            }}
        />
    );
};

export const PersonalInfoCardInline: React.FC<PersonalInfoCardInlineProps> = ({
    editingRowKey,
    onEditingRowKeyChange,
    onCancelAdd
}) => {
    const { t } = useTranslation();
    const theme = useTheme();
    const dispatch = useAppDispatch();
    const adviceClient = useAppSelector((state) => state.adviceClient);
    const adviceGoals = useAppSelector((state) => state.adviceGoals);

    const [showDeleteModal, setShowDeleteModal] = useState(false);
    const [saveErrorType, setSaveErrorType] = useState<'duplicate' | 'generic' | null>(null);
    // AEX-3526: a field-specific confirmation shown when the advisor focuses the DOB/Retirement/Planning
    // age field on an existing profile. Each field is prompted once per edit session.
    const [ageFieldConfirm, setAgeFieldConfirm] = useState<'retirementAge' | 'planningAge' | 'dob' | null>(null);
    const confirmedAgeFieldsRef = useRef<Set<string>>(new Set());
    // Returns the user to where the edit began once the prompt is confirmed.
    const resumeEditRef = useRef<(() => void) | null>(null);

    // AEX-3363: on "Refresh Plan", close the failed edit row so the advisor restarts on fresh data.
    const handlePlanRefreshed = useCallback(() => {
        setSaveErrorType(null);
        onEditingRowKeyChange(null);
    }, [onEditingRowKeyChange]);
    const { isStalePlan, markStaleFromError, refreshPlan } = useStalePlanRefresh(handlePlanRefreshed);

    useEffect(() => {
        return () => {
            dispatch(setHasUnsavedProfileChanges(false));
        };
    }, [dispatch]);

    // Re-arm the per-field prompts whenever a different row enters/leaves edit mode.
    useEffect(() => {
        confirmedAgeFieldsRef.current = new Set();
        resumeEditRef.current = null;
        setAgeFieldConfirm(null);
    }, [editingRowKey]);

    const handleDirtyStateChange = useCallback(
        (dirty: boolean) => {
            dispatch(setHasUnsavedProfileChanges(dirty));
        },
        [dispatch]
    );

    // Cancelling the age prompt exits edit mode and returns the row to its saved view state.
    const handleAgeConfirmCancel = useCallback(() => {
        resumeEditRef.current = null;
        setAgeFieldConfirm(null);
        setSaveErrorType(null);
        onEditingRowKeyChange(null);
    }, [onEditingRowKeyChange]);

    const isClientAvailable = !!adviceClient?.clientFirstName;
    const isCoClientAvailable = !!adviceClient?.relationship?.[0]?.firstName;
    const isFlow2 = getClientJourneyFlowType(adviceClient?.tenant) === 'flow2';
    const isCustomerIdFeatureEnabled = checkFFEnabled(FEATURE_NAMES['CUSTOMER_ID_FEATURE_AEX-3360_AEX-3380']);
    const isParticipantIdAvailable = PARTICIPANT_ID_AVAILABLE_TENANTS.includes(adviceClient?.tenant ?? '');

    const rows: ProfileRow[] = useMemo(() => {
        const data: ProfileRow[] = [];
        if (isClientAvailable) {
            data.push({
                id: 'client',
                type: 'Client',
                customerId: (adviceClient?.participant_id as string) ?? '',
                firstName: adviceClient?.clientFirstName ?? '',
                lastName: adviceClient?.clientLastName ?? '',
                dob: adviceClient?.clientDob ?? '',
                retirementAge: normalizeAge(adviceClient?.retirementAge, '65'),
                planningAge: normalizeAge(adviceClient?.planningAge, '86')
            });
        }
        if (isCoClientAvailable) {
            const coClient = adviceClient.relationship[0];
            data.push({
                id: 'coClient',
                type: 'Co-Client',
                firstName: coClient?.firstName ?? '',
                lastName: coClient?.lastName ?? '',
                dob: coClient?.age ?? '',
                retirementAge: normalizeAge(coClient?.retirementAge, '65'),
                planningAge: normalizeAge(coClient?.planningAge, '86')
            });
        }
        return data;
    }, [adviceClient, isClientAvailable, isCoClientAvailable]);

    const canAddCoClient = isClientAvailable && !isCoClientAvailable;
    const newRowDefaults: Partial<ProfileRow> = isClientAvailable ? emptyCoClientRow : emptyClientRow;

    const validateNameDuplication = (
        field: 'firstName' | 'lastName',
        value: string,
        formValues: ProfileRow
    ): true | string => {
        const first = field === 'firstName' ? value : formValues.firstName ?? '';
        const last = field === 'lastName' ? value : formValues.lastName ?? '';
        if (!String(first).trim() || !String(last).trim()) return true;
        const fullName = `${first} ${last}`.trim().toLowerCase();
        const isRelationship = formValues.type === 'Co-Client';
        if (isRelationship) {
            const clientFull = `${adviceClient?.clientFirstName ?? ''} ${adviceClient?.clientLastName ?? ''}`
                .trim()
                .toLowerCase();
            return fullName !== clientFull || t('CLIENT_COCLIENT_VALIDATION_MESSAGE');
        }
        const coClient = adviceClient?.relationship?.[0];
        if (!coClient) return true;
        const coClientFull = `${coClient.firstName ?? ''} ${coClient.lastName ?? ''}`.trim().toLowerCase();
        return fullName !== coClientFull || t('CLIENT_DUPLICATE_VALIDATION_MESSAGE');
    };

    const validateRetirementAge = (
        value: string,
        birthDate: dayjs.Dayjs | null,
        isRelationship: boolean
    ): true | string => {
        if (!value || !birthDate || !birthDate.isValid()) return true;
        const ageNum = parseInt(value, 10);
        const currentAgeError = isRelationship
            ? t('RETIREMENT_AGE_CURRENT_COCLIENT_AGE_ERROR')
            : t('RETIREMENT_AGE_CURRENT_CLIENT_AGE_ERROR');
        if (Number.isNaN(ageNum) || ageNum <= 0) return currentAgeError;
        if (ageNum > MAX_ALLOWED_RETIREMENT_AGE) return t('RETIREMENT_AGE_GREATER_THAN_ONEEIGHTEEN_ERROR');
        const currentAge = dayjs().diff(birthDate, 'year');
        if (ageNum < currentAge) return currentAgeError;
        return true;
    };

    const validatePlanningAge = (value: string, retirementAge: string): true | string => {
        if (!value) return true;
        const planningAgeNum = parseInt(value, 10);
        if (Number.isNaN(planningAgeNum) || planningAgeNum <= 0) {
            return t('PLANNING_AGE_ABOVE_RETIREMENT_ERROR');
        }
        if (planningAgeNum > MAX_ALLOWED_AGE_FF) return t('PLANNING_AGE_GREATER_THAN_ONETWENTY_ERROR');
        if (retirementAge) {
            const retirementAgeNum = parseInt(retirementAge, 10);
            if (!Number.isNaN(retirementAgeNum) && planningAgeNum <= retirementAgeNum) {
                return t('PLANNING_AGE_ABOVE_RETIREMENT_ERROR');
            }
        }
        return true;
    };

    // Non-blocking warning for an otherwise-valid retirement age (mirrors the old drawer flow).
    // Returns '' when the age is outside the warning bands.
    const getRetirementAgeWarning = (value: string, isRelationship: boolean): string => {
        const ageNum = parseInt(value, 10);
        if (Number.isNaN(ageNum)) return '';
        if (ageNum < DEFAULT_RETIREMENT_AGE) {
            return t('RETIREMENT_AGE_BELOW_MINIMUM_ERROR');
        }
        if (ageNum > MAX_RETIREMENT_AGE_WARNING && ageNum <= MAX_ALLOWED_RETIREMENT_AGE) {
            return isRelationship
                ? t('RETIREMENT_AGE_GREATER_THAN_SEVENTYFIVE_WARNING_COCLIENT')
                : t('RETIREMENT_AGE_GREATER_THAN_SEVENTYFIVE_WARNING');
        }
        return '';
    };

    const renderCustomerIdEditor = (ctx: RowEditContext<ProfileRow>) => {
        if (ctx.row.type === 'Co-Client') {
            return (
                <Box display="flex" alignItems="center" minHeight="40px" sx={{ fontWeight: 400, fontSize: '16px' }}>
                    -
                </Box>
            );
        }
        const savedCustomerId = String(ctx.row.customerId ?? '').trim();
        if (!isParticipantIdAvailable || savedCustomerId) {
            return (
                <Box
                    display="flex"
                    alignItems="center"
                    minHeight="40px"
                    sx={{ whiteSpace: 'nowrap', fontWeight: 400, fontSize: '16px' }}
                >
                    {savedCustomerId || '-'}
                </Box>
            );
        }
        return (
            <Controller
                name="customerId"
                control={ctx.control}
                rules={{
                    required: { value: true, message: t('CUSTOMER_ID_REQUIRED') },
                    maxLength: { value: 36, message: t('CUSTOMER_ID_MAXLENGTH_VALIDATION') },
                    pattern: { value: CUSTOMER_ID_PATTERN, message: t('CUSTOMER_ID_VALIDATION') }
                }}
                render={({ field: f, fieldState }) => (
                    <TextboxInput
                        showTooltipError={!!fieldState.error || saveErrorType !== 'duplicate'}
                        openTooltipError={!!fieldState.error && Object.keys(ctx.errors).length === 1}
                        size="small"
                        fullWidth
                        placeholder={t('CUSTOMER_ID')}
                        value={(f.value as string) ?? ''}
                        onChange={(e) => f.onChange(e.target.value)}
                        onBlur={() => {
                            f.onBlur();
                            runAfterPointerGesture(() => void ctx.trigger('customerId'));
                        }}
                        error={!!fieldState.error || saveErrorType === 'duplicate'}
                        helperText={fieldState.error?.message as string | undefined}
                    />
                )}
            />
        );
    };

    const renderTextEditor = (field: 'firstName' | 'lastName', placeholder: string) => {
        const TextEditor = (ctx: RowEditContext<ProfileRow>) => {
            const siblingField = field === 'firstName' ? 'lastName' : 'firstName';
            const requiredMessage = field === 'firstName' ? t('FIRST_NAME_REQUIRED') : t('LAST_NAME_REQUIRED');
            const maxLengthMessage =
                field === 'firstName' ? t('FIRST_NAME_MAXLENGTH_ERROR') : t('LAST_NAME_MAXLENGTH_ERROR');
            return (
                <Controller
                    name={field}
                    control={ctx.control}
                    rules={{
                        required: { value: true, message: requiredMessage },
                        maxLength: { value: 50, message: maxLengthMessage },
                        minLength: { value: 2, message: '' },
                        pattern: { value: NAME_PATTERN, message: t('NAME_INVALID_CHARACTERS_ERROR') },
                        validate: (value, formValues) => validateNameDuplication(field, String(value ?? ''), formValues)
                    }}
                    render={({ field: f, fieldState }) => (
                        <TextboxInput
                            showTooltipError={true}
                            openTooltipError={!!fieldState.error && Object.keys(ctx.errors).length === 1}
                            size="small"
                            fullWidth
                            placeholder={placeholder}
                            value={(f.value as string) ?? ''}
                            onChange={(e) => {
                                f.onChange(e.target.value);
                                if (ctx.errors[siblingField]) void ctx.trigger(siblingField);
                            }}
                            onBlur={() => {
                                f.onBlur();
                                runAfterPointerGesture(() => void ctx.trigger(field));
                            }}
                            error={!!fieldState.error}
                            helperText={fieldState.error?.message as string | undefined}
                        />
                    )}
                />
            );
        };
        return TextEditor;
    };

    const renderAgeEditor =
        (field: 'retirementAge' | 'planningAge', placeholder: string) =>
        // eslint-disable-next-line react/display-name
        (ctx: RowEditContext<ProfileRow>) => {
            const dobValue = ctx.watch('dob') as string;
            const ageValue = ctx.watch(field) as string;
            const yearLabel = calculateAgeYearLabel(dobValue, ageValue);
            const isRetirement = field === 'retirementAge';
            const requiredMessage = isRetirement ? t('RETIREMENT_AGE_REQUIRED_MSG') : t('PLANNING_AGE_REQUIRED_MSG');
            const patternMessage = isRetirement
                ? t('RETIREMENT_AGE_CURRENT_CLIENT_AGE_ERROR')
                : t('RETIREMENT_PLANNING_RANGE_ERROR_MSG');
            return (
                <Box display="flex" flexDirection="column" gap={0.5}>
                    <Controller
                        name={field}
                        control={ctx.control}
                        rules={{
                            required: { value: true, message: requiredMessage },
                            pattern: { value: /^[0-9]+$/, message: patternMessage },
                            validate: (value, formValues) =>
                                isRetirement
                                    ? validateRetirementAge(
                                          String(value ?? ''),
                                          formValues.dob ? dayjs(formValues.dob, 'DD-MM-YYYY') : null,
                                          formValues.type === 'Co-Client'
                                      )
                                    : validatePlanningAge(String(value ?? ''), formValues.retirementAge)
                        }}
                        render={({ field: f, fieldState }) => {
                            const retirementWarning =
                                isRetirement && !fieldState.error
                                    ? getRetirementAgeWarning(String(f.value ?? ''), ctx.row.type === 'Co-Client')
                                    : '';
                            return (
                                <TextboxInput
                                    showTooltipError={true}
                                    openTooltipError={!!fieldState.error && Object.keys(ctx.errors).length === 1}
                                    warning={!!retirementWarning}
                                    warningText={retirementWarning}
                                    openTooltipWarning={!!retirementWarning && Object.keys(ctx.errors).length === 0}
                                    type="text"
                                    size="small"
                                    fullWidth
                                    placeholder={placeholder}
                                    value={(f.value as string) ?? ''}
                                    onFocus={(e: React.FocusEvent<HTMLInputElement>) => {
                                        const isExisting =
                                            ctx.row.type === 'Client' ? isClientAvailable : isCoClientAvailable;
                                        if (!isExisting || confirmedAgeFieldsRef.current.has(field)) return;
                                        const input = e.target;
                                        resumeEditRef.current = () => input.focus();
                                        setAgeFieldConfirm(field);
                                    }}
                                    onChange={(e) => {
                                        const numericValue = e.target.value.replace(/[^0-9]/g, '');
                                        f.onChange(numericValue);
                                        if (isRetirement && String(ctx.getValues('planningAge') ?? '').trim()) {
                                            void ctx.trigger('planningAge');
                                        }
                                    }}
                                    onBlur={() => {
                                        f.onBlur();
                                        runAfterPointerGesture(() => void ctx.trigger(field));
                                    }}
                                    error={!!fieldState.error}
                                    helperText={fieldState.error?.message as string | undefined}
                                    inputProps={{ maxLength: 3 }}
                                />
                            );
                        }}
                    />
                    {yearLabel && <Typography variant="body.small" color="semantic.text.muted" label={yearLabel} />}
                </Box>
            );
        };

    const renderDobEditor = (ctx: RowEditContext<ProfileRow>) => {
        const isExisting = ctx.row.type === 'Client' ? isClientAvailable : isCoClientAvailable;
        return (
            <DobDatePickerField
                ctx={ctx}
                onRequestConfirm={(resume) => {
                    if (!isExisting || confirmedAgeFieldsRef.current.has('dob')) return;
                    resumeEditRef.current = resume;
                    setAgeFieldConfirm('dob');
                }}
                canOpen={() => !isExisting || confirmedAgeFieldsRef.current.has('dob')}
            />
        );
    };

    const columns: EditableColumnDef<ProfileRow>[] = [
        ...(isCustomerIdFeatureEnabled
            ? [
                  {
                      key: 'customerId',
                      label: t('CUSTOMER_ID'),
                      width: '340px',
                      render: (value: unknown, row: ProfileRow) =>
                          row.type === 'Co-Client' ? (
                              '-'
                          ) : (
                              <Box
                                  component="span"
                                  sx={{ whiteSpace: 'nowrap', color: theme.palette.semantic.text.default }}
                              >
                                  {(value as string) || '-'}
                              </Box>
                          ),
                      renderEdit: renderCustomerIdEditor
                  } as EditableColumnDef<ProfileRow>
              ]
            : []),
        {
            key: 'type',
            label: t('TYPE'),
            width: '9%',
            render: (value) => (
                <Box component="span" sx={{ color: theme.palette.semantic.text.default }}>
                    {t(String(value).toUpperCase())}
                </Box>
            ),
            renderEdit: (ctx) => (
                <Box display="flex" alignItems="center" minHeight="40px" sx={{ fontWeight: 400, fontSize: '16px' }}>
                    {t(String(ctx.row.type).toUpperCase())}
                </Box>
            )
        },
        {
            key: 'firstName',
            label: t('FIRST_NAME_FLOW2'),
            width: '17%',
            render: (value) => (
                <Box component="span" sx={{ color: theme.palette.semantic.text.default }}>
                    {(value as string) || '-'}
                </Box>
            ),
            renderEdit: renderTextEditor('firstName', t('TEXT_ADD_USER_FIRST_NAME_PLACEHOLDER'))
        },
        {
            key: 'lastName',
            label: t('LAST_NAME_FLOW2'),
            width: '17%',
            render: (value) => (
                <Box component="span" sx={{ color: theme.palette.semantic.text.default }}>
                    {(value as string) || '-'}
                </Box>
            ),
            renderEdit: renderTextEditor('lastName', t('TEXT_ADD_USER_LAST_NAME_PLACEHOLDER'))
        },
        {
            key: 'dob',
            label: t('DATE_OF_BIRTH'),
            width: '15%',
            render: (value) => (
                <Box component="span" sx={{ color: theme.palette.semantic.text.default }}>
                    {value ? dayjs(value as string, 'DD-MM-YYYY').format('MMM YYYY') : '-'}
                </Box>
            ),
            renderEdit: renderDobEditor,
            align: 'left'
        },
        {
            key: 'retirementAge',
            label: t('RETIREMENT_AGE'),
            width: '12%',
            render: (value) => (
                <Box component="span" sx={{ color: theme.palette.semantic.text.default }}>
                    {(value as string) || '-'}
                </Box>
            ),
            renderEdit: renderAgeEditor('retirementAge', t('RETIREMENT_AGE')),
            align: 'right'
        },
        {
            key: 'planningAge',
            label: t('PLANNING_AGE'),
            width: '11%',
            render: (value) => (
                <Box component="span" sx={{ color: theme.palette.semantic.text.default }}>
                    {(value as string) || '-'}
                </Box>
            ),
            renderEdit: renderAgeEditor('planningAge', t('PLANNING_AGE')),
            align: 'right'
        }
    ];

    const handleSaveRow = async (values: ProfileRow) => {
        const isRelationship = values.type === 'Co-Client';
        const finalDate = values.dob;

        const clientObject = JSON.parse(JSON.stringify(adviceClient));
        ['loading', 'error', 'hash'].forEach((key) => delete clientObject[key]);

        setSaveErrorType(null);

        try {
            if (isRelationship) {
                const existingRel = Array.isArray(clientObject?.relationship) ? clientObject.relationship[0] : null;
                clientObject.relationship = [
                    {
                        id: existingRel?.id || uuidv4(),
                        firstName: values.firstName,
                        lastName: values.lastName,
                        age: finalDate,
                        relationship: existingRel?.relationship || '',
                        retirementAge: values.retirementAge,
                        planningAge: values.planningAge
                    }
                ];
                clientObject.stages = adviceClient?.stages?.includes(CLIENT_PROFILE_STAGE_KEYS[1])
                    ? adviceClient?.stages
                    : [...(adviceClient?.stages || []), CLIENT_PROFILE_STAGE_KEYS[1]];
                await dispatch(saveClient(clientObject));
            } else if (isClientAvailable) {
                clientObject.clientFirstName = values.firstName;
                clientObject.clientLastName = values.lastName;
                clientObject.clientDob = finalDate;
                clientObject.retirementAge = values.retirementAge;
                clientObject.planningAge = values.planningAge;
                clientObject.stages = adviceClient?.stages?.includes(CLIENT_PROFILE_STAGE_KEYS[1])
                    ? adviceClient?.stages
                    : [...(adviceClient?.stages || []), CLIENT_PROFILE_STAGE_KEYS[1]];
                await dispatch(saveClient(clientObject));
            } else {
                const payloadData = {
                    clientFirstName: values.firstName,
                    clientLastName: values.lastName,
                    clientDob: finalDate,
                    relationship: [],
                    tenant: adviceClient?.tenant,
                    accounts: adviceClient?.accounts,
                    stages: adviceClient?.stages?.includes(CLIENT_PROFILE_STAGE_KEYS[1])
                        ? adviceClient?.stages
                        : [...(adviceClient?.stages || []), CLIENT_PROFILE_STAGE_KEYS[1]],
                    riskProfile: adviceClient?.riskProfile,
                    step: adviceClient?.step,
                    retirementAge: values.retirementAge,
                    planningAge: values.planningAge,
                    ...(isCustomerIdFeatureEnabled && {
                        isParticipantIdAvailable,
                        ...(isParticipantIdAvailable && { participant_id: values.customerId })
                    })
                };
                await dispatch(createClient(payloadData)).unwrap();
            }
        } catch (e) {
            if (!markStaleFromError(e)) {
                const serverMessage = (e as { response?: { data?: { message?: string } } })?.response?.data?.message;
                setSaveErrorType(serverMessage === 'CLIENT_ALREADY_EXISTS' ? 'duplicate' : 'generic');
            }
            throw e;
        }

        let goalsData: AdviceGoalPayloadType = JSON.parse(JSON.stringify(adviceGoals));
        const newClientPlanning = isRelationship
            ? addAgeToDate(adviceClient?.clientDob, adviceClient?.planningAge)
            : addAgeToDate(finalDate, values.planningAge);
        const newClientRetirement = isRelationship
            ? addAgeToDate(adviceClient?.clientDob, adviceClient?.retirementAge)
            : addAgeToDate(finalDate, values.retirementAge);
        const newCoClientPlanning = isRelationship
            ? addAgeToDate(finalDate, values.planningAge)
            : addAgeToDate(adviceClient?.relationship?.[0]?.age, adviceClient?.relationship?.[0]?.planningAge);
        const newCoClientRetirement = isRelationship
            ? addAgeToDate(finalDate, values.retirementAge)
            : addAgeToDate(adviceClient?.relationship?.[0]?.age, adviceClient?.relationship?.[0]?.retirementAge);
        goalsData.formFields = adviceGoals.formFields.map((field) => {
            if (field.data.goalType !== RETIREMENT_GOAL) {
                return field;
            }
            const planStartUpdate =
                field.data.planStartRetirementKey === TargetDateKeys.client
                    ? newClientRetirement
                    : field.data.planStartRetirementKey === TargetDateKeys.coClient
                    ? newCoClientRetirement
                    : undefined;
            const targetDateUpdate =
                field.data.targetDateKey === TargetDateKeys.client
                    ? newClientPlanning
                    : field.data.targetDateKey === TargetDateKeys.coClient
                    ? newCoClientPlanning
                    : undefined;
            // Only clone if updates are needed
            if (!planStartUpdate && !targetDateUpdate) {
                return field;
            }

            return {
                ...field,
                data: {
                    ...field.data,
                    ...(planStartUpdate && { planStartRetirement: planStartUpdate }),
                    ...(targetDateUpdate && { targetDate: targetDateUpdate })
                }
            };
        });
        // Save goals only if there are changes
        if (JSON.stringify(goalsData) !== JSON.stringify(adviceGoals)) await dispatch(saveGoal(goalsData));
        dispatch(setAffectedEwsTerms([...Object.values(EWS_DURATION_KEYS)]));
        dispatch(
            syncAdviceGoals({
                ...adviceGoals,
                response: [],
                formFields: goalsData.formFields,
                global: {
                    ...adviceGoals.global,
                    responseGoalCalculator: [],
                    ...(isFlow2 && { status: STATUSVARIANTS.INCOMPLETE }),
                    enablePDFGenerate: false
                }
            })
        );
    };

    const handleDeleteRow = (row: ProfileRow) => {
        if (row.id === 'coClient') setShowDeleteModal(true);
    };

    const handleDeleteCoClient = async () => {
        try {
            const originalObject: AdviceClient = JSON.parse(JSON.stringify(adviceClient));
            const updatedObject: AdviceClient = { ...originalObject };
            (['loading', 'error'] as const).forEach((key) => delete updatedObject[key]);
            let goalsData: AdviceGoalPayloadType = JSON.parse(JSON.stringify(adviceGoals));
            const coClientId = adviceClient.relationship?.[0]?.id;

            if (
                updatedObject.accounts?.investmentsSources?.length &&
                updatedObject.accounts?.investmentsSources?.length > 0
            ) {
                updatedObject.accounts.investmentsSources = updatedObject.accounts.investmentsSources
                    .map((acc) => {
                        if (acc.accountHolder === 'joint') return { ...acc, accountHolder: 'primary' };
                        if (acc.accountHolder === coClientId) return null;
                        return acc;
                    })
                    .filter((acc): acc is Account => Boolean(acc));
            }

            if (updatedObject.accounts?.incomeSources?.length && updatedObject.accounts?.incomeSources?.length > 0) {
                updatedObject.accounts.incomeSources = updatedObject.accounts.incomeSources
                    .map((inc) => (inc.sourceOwner === 'joint' ? { ...inc, sourceOwner: 'primary' } : inc))
                    .filter((inc) => inc.sourceOwner !== coClientId);
            }

            goalsData.formFields = goalsData.formFields.map((goal) => {
                if (
                    goal.data.goalType === GoalTypes.Retirement &&
                    goal.data.targetDateKey === TargetDateKeys.coClient
                ) {
                    const retirementGoalData: any = goal?.data;
                    retirementGoalData.planStartRetirement = undefined;
                    retirementGoalData.targetDate = undefined;
                }
                if (goal?.data?.sources) {
                    const validInvestmentIds =
                        updatedObject.accounts?.investmentsSources?.map((a) => a.accountId) ?? [];
                    const validIncomeIds = updatedObject.accounts?.incomeSources?.map((a) => a.accountId) ?? [];

                    goal.data.sources.investmentSource =
                        goal.data.sources.investmentSource?.filter((src: { accountNumber: string }) =>
                            validInvestmentIds.includes(src.accountNumber)
                        ) ?? [];
                    goal.data.sources.incomeSource =
                        goal.data.sources.incomeSource?.filter((src: { accountNumber: string }) =>
                            validIncomeIds.includes(src.accountNumber)
                        ) ?? [];
                }
                return goal;
            });

            updatedObject.relationship = [];
            // Deleting the co-client is a Step 1 change: invalidate any previously run plan so the
            // Funding Navigator re-runs EWS (cascading to Goal Calculator and Plan Execution).
            goalsData.response = [];
            goalsData.global = {
                ...goalsData.global,
                responseGoalCalculator: [],
                ...(isFlow2 && { status: STATUSVARIANTS.INCOMPLETE }),
                enablePDFGenerate: false
            };

            setShowDeleteModal(false);
            dispatch(startLoading());
            await dispatch(saveClient(updatedObject));
            // Save client, then sync the co-client cleanup to goals state locally only (no update proposals call).
            dispatch(setAffectedEwsTerms([...Object.values(EWS_DURATION_KEYS)]));
            dispatch(syncAdviceGoals(goalsData));
        } catch (error) {
            console.error('Error deleting Co-Client:', error);
        } finally {
            dispatch(stopLoading());
        }
    };

    return (
        <>
            <SubHeader variant="tables.headers" label={t('PROFILE_SUBHEADER_TEXT')} />
            {isStalePlan && <StalePlanBanner onRefresh={refreshPlan} />}
            {saveErrorType === 'duplicate' && (
                <BannerWrapper>
                    <Banner type="error" message={t('CLIENT_ALREADY_EXISTS')} />
                </BannerWrapper>
            )}
            {saveErrorType === 'generic' && <PdfDownloadErrorBanner i18nKey="SAVE_PLAN_FAILED_BANNER" />}
            <EditableTable<ProfileRow>
                rows={rows}
                columns={columns}
                rowKey="id"
                editingRowKey={editingRowKey}
                onEditingRowKeyChange={(key) => {
                    setSaveErrorType(null);
                    onEditingRowKeyChange(key);
                }}
                newRowDefaults={newRowDefaults}
                onCancelAdd={() => {
                    setSaveErrorType(null);
                    onCancelAdd();
                }}
                addRowLabel={canAddCoClient ? t('ADD_COCLIENT') : undefined}
                onSaveRow={handleSaveRow}
                onDeleteRow={handleDeleteRow}
                canDeleteRow={(row) => row.id === 'coClient'}
                canCancelRow={(row) => row.id !== 'client' || isClientAvailable}
                saveError={!!saveErrorType}
                onClearSaveError={() => setSaveErrorType(null)}
                onDirtyStateChange={handleDirtyStateChange}
                cancelConfirm={{
                    title: t('DRAWER_CLOSE_MODAL_TITLE'),
                    content: t('DRAWER_CLOSE_MODAL_HEADER_TEXT'),
                    stayLabel: t('DRAWER_CLOSE_MODAL_HEADER_TEXT_STAY'),
                    leaveLabel: t('DRAWER_CLOSE_MODAL_HEADER_TEXT_LEAVE')
                }}
                skipSaveIfUnchanged
                actions="auto"
                actionsColumnLabel={t('NEW_ADVISOR_CONSOLE_ACTIONS')}
                actionsColumnWidth="96px"
                validationMode="onChange"
                ariaLabels={{
                    edit: t('EDIT'),
                    delete: t('DELETE'),
                    save: t('SAVE'),
                    cancel: t('CANCEL')
                }}
            />
            {showDeleteModal && (
                <ConfirmationCentered
                    cancelButtonLabel={t(
                        checkFFEnabled(FEATURE_NAMES['NEW_TOOLTIP_FTDS_AEX-3428'])
                            ? 'DELETE_CONFIRMATION_MODAL_CANCEL_TEXT'
                            : 'NO'
                    )}
                    confirmButtonLabel={t(
                        checkFFEnabled(FEATURE_NAMES['NEW_TOOLTIP_FTDS_AEX-3428'])
                            ? 'DELETE_CONFIRMATION_MODAL_DELETE_TEXT'
                            : 'YES'
                    )}
                    title={t(
                        checkFFEnabled(FEATURE_NAMES['NEW_TOOLTIP_FTDS_AEX-3428'])
                            ? 'DELETE_COCLIENT_TITLE_FTDS'
                            : 'DELETING_COCLIENT'
                    )}
                    content={t(
                        checkFFEnabled(FEATURE_NAMES['NEW_TOOLTIP_FTDS_AEX-3428'])
                            ? 'DELETE_COCLIENT_CONTENT_FTDS'
                            : 'REMOVE_CO_CLIENT_ACCOUNTS_ONLY'
                    )}
                    onCancellation={() => setShowDeleteModal(false)}
                    onCloseIconClick={() => setShowDeleteModal(false)}
                    onConfirmation={handleDeleteCoClient}
                    open
                />
            )}
            {ageFieldConfirm && (
                <ConfirmationCentered
                    open
                    title={t(
                        ageFieldConfirm === 'dob'
                            ? 'DOB_CHANGE_TITLE'
                            : ageFieldConfirm === 'retirementAge'
                            ? 'RETIREMENT_AGE_CHANGE_TITLE'
                            : 'PLANNING_AGE_CHANGE_TITLE'
                    )}
                    content={
                        <Trans
                            t={t}
                            i18nKey={
                                ageFieldConfirm === 'retirementAge'
                                    ? 'RETIREMENT_AGE_CHANGE_CONTENT'
                                    : 'PLANNING_AGE_CHANGE_CONTENT'
                            }
                            components={{ br: <br /> }}
                        />
                    }
                    confirmButtonLabel={t('CONTINUE_TEXT')}
                    cancelButtonLabel={t('BUTTON_CANCEL')}
                    onConfirmation={() => {
                        confirmedAgeFieldsRef.current.add(ageFieldConfirm);
                        const resume = resumeEditRef.current;
                        resumeEditRef.current = null;
                        setAgeFieldConfirm(null);
                        if (resume) setTimeout(resume, 0);
                    }}
                    onCancellation={handleAgeConfirmCancel}
                    onCloseIconClick={handleAgeConfirmCancel}
                />
            )}
        </>
    );
};
